'use client';

import React, { useState } from 'react';
import { FileText, Download, Eye, Printer, BarChart2, Calendar, Shield } from 'lucide-react';
import { useRole } from '@/lib/useRole';
import Link from 'next/link';

interface Report {
  id: string;
  name: string;
  description: string;
  category: string;
  lastGenerated?: string;
}

const REPORTS: Report[] = [
  { id: 'r1', name: 'Daily Egg Production Report', description: 'Complete daily production summary with all flocks and houses.', category: 'Production', lastGenerated: '2026-09-04' },
  { id: 'r2', name: 'Weekly Egg Production Report', description: '7-day production totals, averages, and trend analysis.', category: 'Production', lastGenerated: '2026-09-01' },
  { id: 'r3', name: 'Monthly Egg Production Report', description: 'Monthly production summary with month-on-month comparison.', category: 'Production', lastGenerated: '2026-09-01' },
  { id: 'r4', name: 'Flock Production Report', description: 'Per-flock production performance, HDP%, and targets.', category: 'Performance', lastGenerated: '2026-09-04' },
  { id: 'r5', name: 'House Production Report', description: 'Production breakdown by poultry house.', category: 'Performance' },
  { id: 'r6', name: 'Egg Grading Report', description: 'Grading results by size, quality, and intended use.', category: 'Quality', lastGenerated: '2026-09-04' },
  { id: 'r7', name: 'Egg Inventory Report', description: 'Current stock levels, movements, and batch details.', category: 'Inventory', lastGenerated: '2026-09-04' },
  { id: 'r8', name: 'Egg Sales Report', description: 'Sales by customer, grade, and period.', category: 'Commercial' },
  { id: 'r9', name: 'Egg Loss Report', description: 'Damage, spoilage, and rejection analysis.', category: 'Quality', lastGenerated: '2026-09-03' },
  { id: 'r10', name: 'Production Performance Report', description: 'Comprehensive performance scoring across all flocks.', category: 'Performance', lastGenerated: '2026-09-01' },
  { id: 'r11', name: 'Target vs Actual Report', description: 'Actual HDP% compared to configured targets per flock and age.', category: 'Performance' },
  { id: 'r12', name: 'Egg Quality Report', description: 'Quality metrics: crack rate, rejection rate, dirty egg rate trends.', category: 'Quality', lastGenerated: '2026-09-04' },
];

const CATEGORIES = ['All', 'Production', 'Performance', 'Quality', 'Inventory', 'Commercial'];

export default function EggReportsContent() {
  const [category, setCategory] = useState('All');
  const [generating, setGenerating] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const { can } = useRole();

  if (!can('egg_reports.view')) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center">
          <Shield size={28} className="text-red-400" />
        </div>
        <h2 className="text-lg font-bold text-gray-700">Access Denied</h2>
        <p className="text-sm text-gray-400">You do not have permission to view egg production reports.</p>
        <Link href="/egg-production" className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors">
          Back to Overview
        </Link>
      </div>
    );
  }

  const filtered = category === 'All' ? REPORTS : REPORTS.filter(r => r.category === category);

  function handleGenerate(report: Report) {
    setGenerating(report.id);
    setTimeout(() => {
      setGenerating(null);
      setToast(`"${report.name}" generated successfully.`);
      setTimeout(() => setToast(null), 3000);
    }, 1200);
  }

  const categoryColors: Record<string, string> = {
    Production: 'bg-green-100 text-green-700',
    Performance: 'bg-blue-100 text-blue-700',
    Quality: 'bg-amber-100 text-amber-700',
    Inventory: 'bg-purple-100 text-purple-700',
    Commercial: 'bg-teal-100 text-teal-700',
  };

  return (
    <div className="p-6">
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm">
          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />{toast}
        </div>
      )}

      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Egg Production Reports</h1>
          <p className="text-sm text-gray-500 mt-1">Generate, view, and export production reports for any period.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex gap-1">
            {CATEGORIES.map(c => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${category === c ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex gap-2 ml-auto">
            <select className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-green-500">
              <option>Date Range</option>
              <option>Today</option>
              <option>This Week</option>
              <option>This Month</option>
              <option>Custom</option>
            </select>
            <select className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-green-500">
              <option>All Farms</option>
              <option>Idudi Poultry Farm</option>
              <option>Kampala Demo Farm</option>
            </select>
          </div>
        </div>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(report => (
          <div key={report.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                <FileText size={18} className="text-green-600" />
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${categoryColors[report.category] ?? 'bg-gray-100 text-gray-600'}`}>
                {report.category}
              </span>
            </div>
            <h3 className="text-sm font-bold text-gray-900 mb-1">{report.name}</h3>
            <p className="text-xs text-gray-400 mb-3 leading-relaxed">{report.description}</p>
            {report.lastGenerated && (
              <p className="text-xs text-gray-400 mb-3 flex items-center gap-1">
                <Calendar size={11} />
                Last generated: {report.lastGenerated}
              </p>
            )}
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleGenerate(report)}
                disabled={generating === report.id}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60"
              >
                <BarChart2 size={12} />
                {generating === report.id ? 'Generating...' : 'Generate'}
              </button>
              <button className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 transition-colors" title="View">
                <Eye size={14} />
              </button>
              {can('egg_production.export') && (
                <button className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 transition-colors" title="Export PDF">
                  <Download size={14} />
                </button>
              )}
              <button className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 transition-colors" title="Print">
                <Printer size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
